import React, { useState, useCallback, useEffect, CSSProperties } from "react";
import styles from "../index.module.less";
import { Modal, message } from "antd";
import IconFont from "@/components/icon";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { getQRCode, pollingQrcode } from "@/api/scanCode";
import { updateNavigatorOnline } from "@/utils/updateNetStatus";
import sentryManual from "@/utils/sentryManual";
import logger from "@/utils/logger";
import { TeamLoginTypeInfo, TeamLoginTypeRes, AuthDataRes } from "@/types/LoginPage";
import { getHeadImgThumbUrl } from "@/utils/chat/index";
import checkedImg from "@/assets/img/scanCode/checked.png";
import loadingImg from "@/assets/img/scanCode/loading.png";
import colors from "@/components/styles/colors";
import loadingBlack from "@/assets/img/scanCode/loading_black.png";
import { APP_NAME } from "@/config/name";
import loadingWebGov from "@/assets/img/login_gov_code.png";
import { LoginFormContext, LoginFormContextType } from "../../utils";
import AutoLoginCheckbox from "./AutoLoginCheckbox";
import qrRefresh from '@/assets/img/login/qrRefresh.png'
import { Avatar } from "@/baseComponents/Avatar";

const ModalTitle = `${APP_NAME}扫码登录`;

const log = logger.getLogger("MobileInputForm");

const avatarStyle: CSSProperties = {
  width: 18,
  display: "block",
  position: "absolute",
  top: 108,
  left: 110
};
const maskStyle: CSSProperties = {
  position: "absolute",
  width: "100%",
  height: "100%",
  top: 0,
  left: 0,
  background: "rgba(0,0,0,0.7)",
  borderRadius: "10px"
};
const centerStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer"
};

function QrcodeForm(props: any) {
  let scanTimer: any = null;
  let pollingTimer: any = null;
  let waitConfirmTimer: any = null;
  let currLoginType: any = null;

  const {
    changeEgovUrl,
    qrcodeStatus,
    STATUS,
    autoLogin,
    onAutoLoginChange,
    loginType,
    setQrcodeStatus,
    onSelAccount
  } = props;
  const [qrcode, setQrcode] = useState<any>(null);
  const [realName, setRealName] = React.useState("");
  const [avatar, setAvatar] = React.useState("");
  const [isModalVisible, setIsModalVisible] = React.useState(false);

  const { onGlobalError, appRuntimeEnv, service, focusSDK } =
    React.useContext<GlobalContextConfig>(GlobalContext);
  const { selectedTeam } = React.useContext<LoginFormContextType>(LoginFormContext);

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };
  const fastLoginSelectTeam = useCallback(
    async (data: any) => {
      let res;
      try {
        res = await service.authService.fastLogin(
          {
            accessToken: data.twoToken.accessToken,
            userId: data.userId
          },
          {
            "x-token": data.twoToken.accessToken
          }
        );
      } catch (error) {
        console.error(error);
      }
      console.log("fastLoginSelectTeam-res", res);
      if (!res) {
        // '回到初始状态从新扫码'
        clearInterval(pollingTimer);
        // getCode();
        return;
      }
      await handleUserData(res);
      selectedTeam(res);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [service.authService, selectedTeam]
  );

  const handleUserData = useCallback(
    async (data: AuthDataRes) => {
      service.focusService.setToken(data.accessToken);
    },
    [service.focusService]
  );

  function clearTimer() {
    clearInterval(pollingTimer);
    clearTimeout(scanTimer);
    clearTimeout(waitConfirmTimer);
  }

  // 每3秒调一次轮询 二维码 start
  function startPollingTimer(qrcode: any) {
    clearInterval(pollingTimer);
    pollingTimer = setInterval(() => {
      pollingQrcode({ polling: true, pollingUrl: qrcode.pollingUrl })
        .then(({ data }: any) => {
          if (!data) {
            updateNavigatorOnline(false);
            setQrcodeStatus(STATUS.EXPIRED);
            clearTimer();
            return;
          }
          updateNavigatorOnline(true);
          let content = data.content || {};
          if (content) {
            // 轮询接口
            setQrcodeStatus(content.qrStatus);
            setRealName(content.realName);
            setAvatar(content.avatar);
            if (content.qrStatus === STATUS.REDIRECT || content.qrStatus === STATUS.EXPIRED) {
              clearInterval(pollingTimer);
              clearTimeout(waitConfirmTimer);
              if (content.qrStatus === STATUS.REDIRECT) {
                setTimeout(() => {
                  // '登陆成功
                  fastLoginSelectTeam(content);
                }, 1000);
              }
            } else if (content.qrStatus === STATUS.WAIT_CONFIRM) {
              // '扫码成功'
              setWaitConfirmTimer(1000 * qrcode.waitScanExpire);
            } else if (content.qrStatus === STATUS.CANCEL) {
              // '回到初始状态从新扫码'
              clearInterval(pollingTimer);
              getCode();
            } else if (content.qrStatus === STATUS.TICKET_LOGIN) {
              onSelAccount(content, "qrcode");
            }
          } else {
            setQrcodeStatus(STATUS.EXPIRED);
            clearTimer();
          }
        })
        .catch((err) => {
          updateNavigatorOnline(false);
          log.debug("pollingStatus-error", err);
          sentryManual.captureException(err, "pollingStatus-error");
          focusSDK.printLog("pollingStatus-error", err?.stack || err?.message || err);
          setQrcodeStatus(STATUS.EXPIRED);
        });
    }, 3000);
  }
  useEffect(() => {
    clearTimer();
    getCode();
    return () => {
      clearTimer();
    };
  }, []);

  function getCode() {
    setQrcodeStatus(STATUS.FETCHING); // 正在请求状态
    getQRCode({ polling: false })
      .then(({ data }) => {
        focusSDK.printLog("getCode-data", data);
        if (!data) {
          updateNavigatorOnline(false);
          setQrcodeStatus(STATUS.EXPIRED);
          return;
        }
        updateNavigatorOnline(true);
        if (data.errorCode === "0") {
          let content = data.content || {};
          setQrcode(content);
          setQrcodeStatus(STATUS.WAIT_SCAN); // 等待扫码
          // 获取到二维码后等待扫码
          // 扫描成功后计时
          waitScanTimeout(1000 * content.waitScanExpire);
          // 轮训登录
          startPollingTimer(content);
        } else {
          setQrcodeStatus(STATUS.EXPIRED);
        }
      })
      .catch((err) => {
        updateNavigatorOnline(false);
        log.debug("getCode-error", err);
        sentryManual.captureException(err, "getCode-error");
        focusSDK.printLog("getCode-error", err?.stack || err?.message || err);
        setQrcodeStatus(STATUS.EXPIRED);
      });
  }
  function waitScanTimeout(time: any) {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(() => {
      // '二维码已过期');
      setQrcodeStatus(STATUS.EXPIRED); // 等待扫码
    }, time);
  }
  function setWaitConfirmTimer(time: any) {
    clearTimeout(waitConfirmTimer);
    clearTimeout(scanTimer);
    waitConfirmTimer = setTimeout(() => {
      //  等待确认登录超时');
      setQrcodeStatus(STATUS.EXPIRED); // 等待确认跳转页面
    }, time);
  }

  function UserHead(name: string, avatar: string) {
    const avatarPic = (
      <div>
        <Avatar
          src=""
          name={name}
          styleObj={{
            width: 50,
            height: 50,
            fontSize: 16
          }}
        />
        {/* <img
          style={{ width: 50, borderRadius: 8, height: 50, objectFit: "cover" }}
          src={changeEgovUrl(getHeadImgThumbUrl(avatar, 150, 150))}
          className="avatar-mask"
        /> */}
        <img
          src={changeEgovUrl(checkedImg)}
          style={{
            ...avatarStyle
          }}
        />
      </div>
    );
    // const avatarText = (
    //   <div>
    //     <p style={{ margin: 0 }} className="avatar-first-chr">
    //       {name ? name.slice(0, 1) : ""}
    //     </p>
    //     <img
    //       src={changeEgovUrl(checkedImg)}
    //       style={{
    //         ...avatarStyle
    //       }}
    //     />
    //   </div>
    // );
    // return avatar ? avatarPic : avatarText;
    return avatarPic;
  }
  // 二维码 end
  return (
    <>
      {qrcodeStatus !== STATUS.WAIT_CONFIRM && qrcodeStatus !== STATUS.REDIRECT && (
        <div className={styles.qrTitle}>扫码登录</div>
      )}
      <div
        className={styles["scan-code"]}
        id="scan-code"
        style={{
          marginTop:
            qrcodeStatus === STATUS.WAIT_CONFIRM || qrcodeStatus === STATUS.REDIRECT ? 100 : 0
        }}
      >
        <div className={styles["qrCode-img"]}>
          {qrcodeStatus !== STATUS.WAIT_CONFIRM && qrcodeStatus !== STATUS.REDIRECT && (
            <p>使用{APP_NAME}移动端扫描二维码</p>
          )}
          <div className={styles.img}>
            <div style={{width: "100%", height: "100%"}}> 
              <img src={changeEgovUrl(qrcode?.qrCodeUrl)} alt="" />
              {!qrcode && <div>获取不到二维码</div>}
            </div>
            {/* 过期 */}
            {qrcodeStatus === STATUS.EXPIRED ? (
              <div style={{ ...maskStyle, ...centerStyle }} onClick={getCode}>
                {/* <IconFont
                  type="iconjs_ic_break"
                  style={{
                    fontSize: "28px",
                    color: "#ffffff"
                  }}
                /> */}
                <img style={{ width: 40 }} src={qrRefresh} />
                <p
                  style={{
                    fontSize: 16,
                    color: "#fff",
                    margin: "10px 0 0 0"
                  }}
                >
                  刷新二维码
                </p>
              </div>
            ) : null}
            {/* 正在加载 */}
            {qrcodeStatus === STATUS.FETCHING || qrcodeStatus === STATUS.WAIT_REDIRECT ? (
              <div style={{ ...maskStyle, ...centerStyle }}>
                <img style={{ width: 20 }} className={styles.xuanzhuan} src={changeEgovUrl(loadingImg)} />

                <p
                  style={{
                    fontSize: 14,
                    color: "#fff",
                    margin: "10px 0 0 0"
                  }}
                >
                  加载中...
                </p>
              </div>
            ) : null}
            {/* 扫码成功 */}
            {qrcodeStatus === STATUS.WAIT_CONFIRM ||
            qrcodeStatus === STATUS.REDIRECT ||
            qrcodeStatus === STATUS.WAIT_REDIRECT ? (
              <>
                <div
                  style={{
                    ...maskStyle,
                    ...centerStyle,
                    background:
                      qrcodeStatus === STATUS.WAIT_CONFIRM
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.6)"
                  }}
                >
                  {UserHead(realName, avatar)}
                </div>
                <h1
                  style={{
                    lineHeight: 1,
                    color: `${colors.black2}`,
                    fontSize: 20,
                    marginTop: 32,
                    marginBottom: 12
                  }}
                >
                  扫码成功
                </h1>
                <p
                  style={{
                    lineHeight: 1,
                    color: "#6E6F71",
                    fontSize: 12
                  }}
                >
                  {qrcodeStatus === STATUS.REDIRECT || qrcodeStatus === STATUS.WAIT_REDIRECT ? (
                    <>
                      正在登录
                      <img width="12" className={styles.xuanzhuan} src={changeEgovUrl(loadingBlack)} />
                    </>
                  ) : (
                    "请在移动端确认登录"
                  )}
                </p>
              </>
            ) : (
              <div style={{ marginTop: "20px", color: "#6A6A6A", display: "fix", justifyContent: "center" }}>
                <AutoLoginCheckbox
                  autoLogin={autoLogin}
                  qrcodeStatus={qrcodeStatus}
                  STATUS={STATUS}
                  onAutoLoginChange={onAutoLoginChange}
                  loginType={loginType}
                />
              </div>
            )}
          </div>
        </div>
        <Modal
          className={styles.Qcode}
          title={ModalTitle}
          visible={isModalVisible}
          onOk={handleOk}
          onCancel={handleCancel}
          centered={true}
          footer={null}
          width={646}
        >
          <img src={changeEgovUrl(loadingWebGov)} />
        </Modal>
      </div>
    </>
  );
}

export default QrcodeForm;
